import type { Vec2 } from './math/vec2';

export type EntityId = number;

/** What kind of agent controls an entity. `remote` is reserved for future
 *  networked players so the simulation already has a slot for them. */
export type EntityKind = 'player' | 'bot' | 'remote';

/** Bot AI decision states. */
export type BotMode = 'wander' | 'hunt' | 'flee';

export interface BotState {
  mode: BotMode;
  /** Sim time at which this bot will re-evaluate its decision. */
  nextThinkTime: number;
  /** Sim time at which a wandering bot will pick a new heading. */
  nextWanderTime: number;
  /** Cached heading the bot is currently steering toward. */
  heading: Vec2;
  /** Id of the entity this bot is hunting or fleeing, if any. */
  targetId: EntityId | null;
}

/**
 * Abstract, device-independent expression of "what this entity wants to do
 * this tick". Input sources and bot AI both produce Intents; the simulation
 * only ever reads Intents — never raw mouse/touch/keys. This is the contract
 * that lets desktop, mobile, bots, and (later) the network all drive the same
 * logic.
 */
export interface Intent {
  /** Desired movement direction. Magnitude is an analog throttle in [0, 1]. */
  moveDir: Vec2;
  /**
   * Queued activations per action id (e.g. "grow", "shrink"). Fractional values
   * are allowed (bots accumulate clicks continuously). Actions consume from
   * here, rate-limited by a token bucket. New actions (split, eject, ...) just
   * add a new key — no change to this shape.
   */
  pulses: Record<string, number>;
}

export interface Entity {
  id: EntityId;
  kind: EntityKind;
  name: string;
  pos: Vec2;
  vel: Vec2;
  /** Position at the start of the previous step — used for render interpolation. */
  prevPos: Vec2;
  /** Radius in world units. Mass = size^2 (area-proportional). */
  size: number;
  alive: boolean;
  /** Cosmetic skin id (resolved to a color by the renderer/skins catalog). */
  skinId: string;
  /** Per-action rate-limit token buckets, keyed by action id. */
  rateTokens: Record<string, number>;
  /** Remaining seconds of the post-shrink speed boost. */
  boostTimer: number;
  /** Largest size reached this life — the size term of the run score. */
  peakSize: number;
  /** Total prey mass eaten this life (a continuous scoring factor). */
  massEaten: number;
  /** Number of prey eaten this life (a scoring factor / tiebreaker). */
  kills: number;
  /** Simulation time (seconds) when this entity was created — for survival time. */
  bornAt: number;
  /** Bot AI state (only present for bots). */
  ai?: BotState;
  /** Intent for this entity this tick (set by input or bot AI before stepping). */
  intent: Intent;
}

/** A fresh, zeroed intent. */
export function emptyIntent(): Intent {
  return { moveDir: { x: 0, y: 0 }, pulses: {} };
}
